import * as THREE from 'three';
import { DirtyRange, FOG_FRAG, FOG_VERT, fxUniforms, quadGeometry } from './common';

/** Draws a glowing rune circle into a canvas (white; tinted per instance). */
function runeTexture(): THREE.CanvasTexture {
  const S = 512, c = document.createElement('canvas'); c.width = c.height = S;
  const g = c.getContext('2d')!, h = S / 2;
  g.translate(h, h);
  g.strokeStyle = g.fillStyle = '#fff';
  g.lineCap = 'round';
  const circle = (r: number, w: number, a = 1) => { g.globalAlpha = a; g.lineWidth = w; g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.stroke(); };
  circle(h * 0.96, 5); circle(h * 0.86, 2.5, 0.8); circle(h * 0.58, 3, 0.9); circle(h * 0.5, 1.5, 0.6);
  // runes between the outer rings
  const n = 24;
  for (let i = 0; i < n; i++) {
    g.save(); g.rotate((i / n) * Math.PI * 2); g.translate(0, -h * 0.91); g.globalAlpha = 0.95; g.lineWidth = 3;
    g.beginPath();
    const k = (i * 7) % 5;
    if (k === 0) { g.moveTo(-7, -9); g.lineTo(0, 9); g.lineTo(7, -9); }
    else if (k === 1) { g.moveTo(0, -10); g.lineTo(0, 10); g.moveTo(-7, -2); g.lineTo(7, 4); }
    else if (k === 2) { g.arc(0, 0, 7, 0.4, Math.PI * 2 - 0.4); }
    else if (k === 3) { g.moveTo(-8, 8); g.lineTo(0, -9); g.lineTo(8, 8); g.moveTo(-4, 2); g.lineTo(4, 2); }
    else { g.moveTo(-8, 0); g.lineTo(8, 0); g.moveTo(0, -9); g.lineTo(0, 9); }
    g.stroke(); g.restore();
  }
  // inner star of chords
  g.globalAlpha = 0.7; g.lineWidth = 2;
  const pts = 8;
  g.beginPath();
  for (let i = 0; i <= pts; i++) { const a = (i * 3 / pts) * Math.PI * 2; const x = Math.cos(a) * h * 0.58, y = Math.sin(a) * h * 0.58; if (i) g.lineTo(x, y); else g.moveTo(x, y); }
  g.stroke();
  // ticks on the inner ring
  for (let i = 0; i < 48; i++) { g.save(); g.rotate((i / 48) * Math.PI * 2); g.globalAlpha = 0.8; g.lineWidth = i % 4 ? 1.5 : 3; g.beginPath(); g.moveTo(0, -h * 0.58); g.lineTo(0, -h * (i % 4 ? 0.62 : 0.66)); g.stroke(); g.restore(); }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/**
 * Instanced additive rune circles lying on the altar under high-grade relics.
 * Per instance: position, colour × intensity, size, rotation — one draw call.
 */
export class Runes {
  readonly mesh: THREE.Mesh;
  private readonly pos: THREE.InstancedBufferAttribute;
  private readonly col: THREE.InstancedBufferAttribute;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly dirty = new DirtyRange();
  private free: number[] = [];
  private top = 0;

  constructor(readonly capacity = 64) {
    const g = (this.geo = quadGeometry());
    this.pos = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage); // xyz + rotation
    this.col = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(THREE.DynamicDrawUsage); // rgb*I + size
    g.setAttribute('iPos', this.pos); g.setAttribute('iCol', this.col);
    g.instanceCount = 0;
    const mat = new THREE.ShaderMaterial({
      uniforms: { ...fxUniforms, uMap: { value: runeTexture() } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute vec4 iPos; attribute vec4 iCol;
        varying vec2 vUv; varying vec3 vCol;
        ${FOG_VERT}
        void main() {
          vUv = uv; vCol = iCol.rgb;
          float c = cos(iPos.w), s = sin(iPos.w);
          vec2 q = mat2(c, -s, s, c) * position.xy * iCol.w;
          vec4 mv = modelViewMatrix * vec4(iPos.xyz + vec3(q.x, 0.0, -q.y), 1.0);
          FOG_FROM_MV(mv)
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform sampler2D uMap;
        varying vec2 vUv; varying vec3 vCol;
        ${FOG_FRAG}
        void main() {
          float a = texture2D(uMap, vUv).a;
          gl_FragColor = vec4(vCol * a * fogKeep(), 1.0);
        }`,
    });
    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 3;
  }

  alloc(): number {
    const i = this.free.pop() ?? this.top++;
    if (i >= this.capacity) throw new Error('Runes capacity exceeded');
    this.geo.instanceCount = Math.max(this.geo.instanceCount, i + 1);
    this.set(i, 0, -10, 0, 0, _c.setRGB(0, 0, 0), 0, 0);
    return i;
  }
  release(i: number) { this.set(i, 0, -10, 0, 0, _c.setRGB(0, 0, 0), 0, 0); this.free.push(i); }

  set(i: number, x: number, y: number, z: number, rot: number, color: THREE.Color, intensity: number, size: number) {
    this.pos.setXYZW(i, x, y, z, rot);
    this.col.setXYZW(i, color.r * intensity, color.g * intensity, color.b * intensity, size);
    this.dirty.mark(i);
  }

  flush() { this.dirty.flush([this.pos, this.col]); }
}
const _c = new THREE.Color();
